import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc,
  setDoc,
  addDoc, 
  deleteDoc,
  updateDoc, 
  onSnapshot, 
  serverTimestamp
} from 'firebase/firestore';
import { 
  getAuth, 
  signInAnonymously, 
  onAuthStateChanged,
  signInWithCustomToken
} from 'firebase/auth';
import { 
  TreeDeciduous, 
  Sprout, 
  Apple, 
  Menu, 
  X, 
  MapPin, 
  Phone, 
  Clock, 
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Mail,
  Settings,
  Lock,
  LogOut,
  Globe,
  MessageSquare,
  Image as ImageIcon,
  Key,
  Plus,
  Trash2,
  Edit,
  Users,
  Building,
  FileWarning,
  Eye,
  Heart,
  List,
  CheckSquare,
  Link as LinkIcon,
  ExternalLink,
  Maximize2 
} from 'lucide-react';

// --- Firebase Configuration & Initialization ---
const firebaseConfig = {
  apiKey: "AIzaSyA7cnA1TpABteNmdCPQdrbKRtDsDpLoaPo",
  authDomain: "tree-kids-school-hp.firebaseapp.com",
  projectId: "tree-kids-school-hp",
  storageBucket: "tree-kids-school-hp.firebasestorage.app",
  messagingSenderId: "684884740378",
  appId: "1:684884740378:web:2779e9e65bdcfe6a9584a3",
  measurementId: "G-359KHXF9ZW"
};

// Initialize Firebase only once
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  if (!/already exists/.test(error.message)) {
    console.error('Firebase initialization error', error);
  }
}
const auth = getAuth(app);
const db = getFirestore(app);

const appId = (typeof window !== 'undefined' && window.__app_id) ? window.__app_id : 'tree-kids-school-default';

// --- Color & Style Constants (Brand Design) ---
const COLORS = {
  base: 'bg-[#FFFEFA]', 
  mainGreen: 'text-[#8FC358]',
  bgMainGreen: 'bg-[#8FC358]',
  borderGreen: 'border-[#8FC358]',
  accentRed: 'text-[#E65227]',
  bgAccentRed: 'bg-[#E65227]',
  textBrown: 'text-[#5A4230]',
  bgGray: 'bg-[#9FA0A0]',
  softBgGreen: 'bg-[#F1F8E9]',
};

// --- Custom Components ---
const WaveSeparator = ({ color = "#FFFEFA", position = "bottom" }) => (
  <div className={`absolute left-0 w-full overflow-hidden leading-none z-10 ${position === 'top' ? 'top-0 rotate-180' : 'bottom-0'}`}>
    <svg className="relative block w-[calc(100%+1.3px)] h-[40px] md:h-[60px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill={color}></path>
    </svg>
  </div>
);

const MarkerUnderline = ({ color = "#E65227", className = "" }) => (
  <span className={`absolute bottom-1 left-0 w-full h-3 -z-10 opacity-30 rounded-sm transform -rotate-1 ${className}`} style={{ backgroundColor: color }}></span>
);

// --- Helpers ---
const generateId = () => Math.random().toString(36).substr(2, 9);

const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Image Compression Helper
const compressImage = (file, maxWidth = 800, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const outputType = 'image/jpeg'; 
        const dataUrl = canvas.toDataURL(outputType, quality);
        
        const sizeInBytes = Math.ceil((dataUrl.length - `data:${outputType};base64,`.length) * 3 / 4);
        
        if (sizeInBytes > 500000 && maxWidth > 200) { 
           resolve(compressImage(file, maxWidth * 0.7, quality - 0.1));
        } else {
           resolve(dataUrl);
        }
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

// デフォルトのお問い合わせフォーム設定
const DEFAULT_CONTACT_CONFIG = {
  title: "お問い合わせフォーム",
  fields: [
    { id: 'f_location', label: 'お問い合わせ先', type: 'select', required: false, useLocationList: true },
    { id: 'f_name', label: 'お名前', type: 'text', required: true },
    { id: 'f_email', label: 'メールアドレス', type: 'email', required: true },
    { id: 'f_message', label: 'お問い合わせ内容', type: 'textarea', required: true }
  ]
};

// --- Main App Component ---
export default function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('home'); 
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [locations, setLocations] = useState([]); 
  
  const [contactConfig, setContactConfig] = useState(DEFAULT_CONTACT_CONFIG);

  const [siteSettings, setSiteSettings] = useState({
    seoTitle: 'Tree Kids School Choice',
    seoDescription: '名古屋市緑区の放課後等デイサービス。',
    seoKeywords: '放課後等デイサービス, 名古屋市, 緑区, 発達支援',
    notificationEmail: 'info@treekids-choice.com',
    logoMarkUrl: '', 
    logoTextUrl: '', 
    heroImageUrl: '',
    heroVideoUrl: '', 
    adminPassword: 'admin123'
  });

  // Inject Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Zen+Maru+Gothic:wght@400;500;700;900&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Error Logging
  useEffect(() => {
    const logErrorToFirestore = async (errorMsg, errorInfo = {}) => {
      try {
        if (!appId) return;
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'system_errors'), {
          message: errorMsg.toString(),
          info: JSON.stringify(errorInfo),
          userAgent: navigator.userAgent,
          url: window.location.href,
          createdAt: serverTimestamp(),
          resolved: false
        });
      } catch (e) {
        console.error("Failed to report error:", e);
      }
    };
    const handleGlobalError = (event) => {
      logErrorToFirestore(event.message || "Unknown Error", {
        filename: event.filename, lineno: event.lineno, colno: event.colno
      });
    };
    window.addEventListener('error', handleGlobalError);
    return () => window.removeEventListener('error', handleGlobalError);
  }, []); 

  // Authentication
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof window !== 'undefined' && window.__initial_auth_token) {
          await signInWithCustomToken(auth, window.__initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (error) {
        console.warn("Auth fallback", error);
        try { await signInAnonymously(auth); } catch (e) { console.error(e); }
      }
    };
    initAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, setUser);
    return () => unsubscribeAuth();
  }, []);

  // Fetch Data
  useEffect(() => {
    if (!user) return;

    const settingsRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config');
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        setSiteSettings(prev => ({ ...prev, ...docSnap.data() }));
      }
    });

    const contactConfigRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'contact_form');
    const unsubscribeContactConfig = onSnapshot(contactConfigRef, (docSnap) => {
      if (docSnap.exists()) {
        setContactConfig(docSnap.data());
      } else {
        setDoc(contactConfigRef, DEFAULT_CONTACT_CONFIG);
      }
    });

    const locationsRef = collection(db, 'artifacts', appId, 'public', 'data', 'locations');
    const unsubscribeLocations = onSnapshot(locationsRef, (snapshot) => {
      const locs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLocations(locs);
    });

    return () => {
      unsubscribeSettings();
      unsubscribeLocations();
      unsubscribeContactConfig();
    };
  }, [user]);

  // SEO & Favicon
  useEffect(() => {
    document.title = siteSettings.seoTitle;
    const updateMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    updateMeta('description', siteSettings.seoDescription);
    updateMeta('keywords', siteSettings.seoKeywords);

    if (siteSettings.logoMarkUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = siteSettings.logoMarkUrl;
    }
  }, [siteSettings]);

  const globalStyles = `
    body {
      font-family: 'Zen Maru Gothic', sans-serif;
      color: #5A4230;
    }
    .texture-bg {
      background-color: #FFFEFA;
      background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E");
    }
    .hand-drawn-border {
      border-radius: 255px 15px 225px 15px / 15px 225px 15px 255px;
    }
    .hand-drawn-box {
      border-radius: 60px 40px 50px 30px / 30px 50px 40px 60px;
    }
  `;

  return (
    <>
      <style>{globalStyles}</style>
      {currentPage === 'admin' ? (
        <AdminPanel 
          user={user} 
          settings={siteSettings} 
          locations={locations}
          contactConfig={contactConfig} 
          isAuthenticated={isAdminAuthenticated}
          setIsAuthenticated={setIsAdminAuthenticated}
          onBackToHome={() => setCurrentPage('home')}
        />
      ) : (
        <PublicSite 
          user={user} 
          onGoToAdmin={() => setCurrentPage('admin')}
          settings={siteSettings}
          locations={locations}
          contactConfig={contactConfig} 
        />
      )}
    </>
  );
}

// --- Admin Component (Functional & Clean) ---
function AdminPanel({ user, settings, locations, contactConfig, isAuthenticated, setIsAuthenticated, onBackToHome }) {
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('news');
  const [localSettings, setLocalSettings] = useState(settings);
  const [newsForm, setNewsForm] = useState({ title: '', date: '', content: '', imageUrl: '' });
  const [newsList, setNewsList] = useState([]);
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]); 
  const [editingLocationId, setEditingLocationId] = useState(null);
  // galleryImages を初期値に追加
  const [locationForm, setLocationForm] = useState({ name: '', address: '', phone: '', description: '', imageUrl: '', galleryImages: [], features: [], staff: [] });
  const [staffForm, setStaffForm] = useState({ id: '', name: '', role: '', bio: '', imageUrl: '' });
  const [isEditingStaff, setIsEditingStaff] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [uploadError, setUploadError] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // リンク管理用ステート
  const [linkForm, setLinkForm] = useState({ title: '', url: '', imageUrl: '' });
  const [linksList, setLinksList] = useState([]);
  const [editingLinkId, setEditingLinkId] = useState(null);

  // お問い合わせ設定用State
  const [inquirySubTab, setInquirySubTab] = useState('list');
  const [editingContactConfig, setEditingContactConfig] = useState(contactConfig);

  useEffect(() => { setLocalSettings(settings); }, [settings]);
  useEffect(() => { setEditingContactConfig(contactConfig); }, [contactConfig]);

  useEffect(() => {
    if (!user || !isAuthenticated) return;
    
    const newsRef = collection(db, 'artifacts', appId, 'public', 'data', 'news');
    const unsubscribeNews = onSnapshot(newsRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      items.sort((a, b) => new Date(b.date) - new Date(a.date));
      setNewsList(items);
    });

    const linksRef = collection(db, 'artifacts', appId, 'public', 'data', 'external_links');
    const unsubscribeLinks = onSnapshot(linksRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLinksList(items);
    });

    const inquiriesRef = collection(db, 'artifacts', appId, 'public', 'data', 'inquiries_admin_view');
    const unsubscribeInquiries = onSnapshot(inquiriesRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      items.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
      setInquiries(items);
    });

    const errorsRef = collection(db, 'artifacts', appId, 'public', 'data', 'system_errors');
    const unsubscribeErrors = onSnapshot(errorsRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      items.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
      setErrorLogs(items);
    });

    return () => { 
      unsubscribeNews();
      unsubscribeLinks();
      unsubscribeInquiries(); 
      unsubscribeErrors(); 
    };
  }, [user, isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    const currentAdminPass = settings.adminPassword || 'admin123';
    if (password === currentAdminPass) setIsAuthenticated(true);
    else alert('パスワードが違います');
  };

  const handleSaveSettings = async (updatedSettings = localSettings) => {
    if (!user) return;
    setSaveStatus('saving');
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config'), updatedSettings);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) { console.error(error); setSaveStatus('error'); }
  };

  const handleSaveContactConfig = async () => {
    if (!user) return;
    setSaveStatus('saving');
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'contact_form'), editingContactConfig);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) { console.error(error); setSaveStatus('error'); }
  };

  const addField = () => {
    const newField = {
      id: generateId(),
      label: '新しい項目',
      type: 'text',
      required: false,
      options: ''
    };
    setEditingContactConfig(prev => ({ ...prev, fields: [...prev.fields, newField] }));
  };

  const removeField = (index) => {
    const newFields = [...editingContactConfig.fields];
    newFields.splice(index, 1);
    setEditingContactConfig(prev => ({ ...prev, fields: newFields }));
  };

  const updateField = (index, key, value) => {
    const newFields = [...editingContactConfig.fields];
    newFields[index][key] = value;
    setEditingContactConfig(prev => ({ ...prev, fields: newFields }));
  };

  const moveField = (index, direction) => {
    const newFields = [...editingContactConfig.fields];
    if (direction === 'up' && index > 0) {
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    } else if (direction === 'down' && index < newFields.length - 1) {
      [newFields[index + 1], newFields[index]] = [newFields[index], newFields[index + 1]];
    }
    setEditingContactConfig(prev => ({ ...prev, fields: newFields }));
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      if (editingNewsId) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'news', editingNewsId), { ...newsForm, updatedAt: serverTimestamp() });
        alert("お知らせを更新しました"); setEditingNewsId(null);
      } else {
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'news'), { ...newsForm, createdAt: serverTimestamp() });
        alert("お知らせを投稿しました");
      }
      setNewsForm({ title: '', date: '', content: '', imageUrl: '' });
    } catch (error) { console.error(error); }
  };
  const handleEditNews = (item) => { setNewsForm({ title: item.title, date: item.date, content: item.content, imageUrl: item.imageUrl || '' }); setEditingNewsId(item.id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleCancelEdit = () => { setNewsForm({ title: '', date: '', content: '', imageUrl: '' }); setEditingNewsId(null); };
  const handleDeleteNews = async (id) => { if (window.confirm("本当にこのお知らせを削除しますか？")) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'news', id)); };
  
  // リンク管理機能
  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      if (editingLinkId) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'external_links', editingLinkId), { ...linkForm, updatedAt: serverTimestamp() });
        alert("リンクを更新しました");
        setEditingLinkId(null);
      } else {
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'external_links'), { ...linkForm, createdAt: serverTimestamp() });
        alert("リンクを追加しました");
      }
      setLinkForm({ title: '', url: '', imageUrl: '' });
    } catch (error) { console.error(error); }
  };
  const handleEditLink = (item) => { setLinkForm({ title: item.title, url: item.url, imageUrl: item.imageUrl || '' }); setEditingLinkId(item.id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleCancelLinkEdit = () => { setLinkForm({ title: '', url: '', imageUrl: '' }); setEditingLinkId(null); };
  const handleDeleteLink = async (id) => { if (window.confirm("本当にこのリンクを削除しますか？")) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'external_links', id)); };

  // resetLocationForm で galleryImages も初期化
  const resetLocationForm = () => { setLocationForm({ name: '', address: '', phone: '', description: '', imageUrl: '', galleryImages: [], features: [], staff: [] }); setEditingLocationId(null); setShowLocationForm(false); };
  
  const handleEditLocation = (loc) => { 
    // 既存データに galleryImages がない場合のフォールバック
    setLocationForm({ ...loc, galleryImages: loc.galleryImages || [] }); 
    setEditingLocationId(loc.id); 
    setShowLocationForm(true); 
  };
  const handleDeleteLocation = async (id) => { if (window.confirm('本当に削除しますか？')) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'locations', id)); };
  
  const handleSaveLocation = async (e) => { 
    e.preventDefault(); 
    setSaveStatus('saving'); 
    try { 
      if (editingLocationId) await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'locations', editingLocationId), locationForm); 
      else await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'locations'), locationForm); 
      setSaveStatus('saved'); 
      resetLocationForm(); 
      setTimeout(() => setSaveStatus('idle'), 2000); 
    } catch (e) { 
      console.error(e); 
      setSaveStatus('error'); 
      if (e.message && e.message.includes('exceeds the maximum allowed size')) {
         alert("保存に失敗しました。画像の枚数が多すぎるか、データ量が大きすぎます。画像を削除するか、さらに小さくしてください。");
      } else {
         alert("保存に失敗しました。画像サイズが大きすぎる可能性があります。");
      }
    } 
  };

  // 画像アップロードの分岐ロジック
  // isGallery: ギャラリー画像追加フラグ
  const handleImageUpload = async (field, e, isStaff = false, isNews = false, isLink = false, isGallery = false) => { 
    const file = e.target.files[0]; 
    if (!file) return; 
    if (!file.type.startsWith('image/')) { setUploadError('画像のみ可能です'); return; } 
    
    setIsCompressing(true); 
    setUploadError(''); 
    
    try { 
      const maxWidth = isStaff ? 300 : 800;
      const quality = isStaff ? 0.5 : 0.6;
      
      const compressedDataUrl = await compressImage(file, maxWidth, quality); 
      
      if (isNews) {
        setNewsForm(prev => ({ ...prev, imageUrl: compressedDataUrl })); 
      } else if (isLink) {
        setLinkForm(prev => ({ ...prev, imageUrl: compressedDataUrl }));
      } else if (isStaff) {
        setStaffForm(prev => ({ ...prev, imageUrl: compressedDataUrl })); 
      } else if (isGallery) {
        // ギャラリー画像の場合は配列に追加
        setLocationForm(prev => ({ ...prev, galleryImages: [...(prev.galleryImages || []), compressedDataUrl] }));
      } else if (showLocationForm) {
        setLocationForm(prev => ({ ...prev, imageUrl: compressedDataUrl })); 
      } else {
        setLocalSettings(prev => ({ ...prev, [field]: compressedDataUrl })); 
      }
    } catch (error) { 
      console.error(error); 
      setUploadError('画像の圧縮に失敗しました'); 
    } finally { 
      setIsCompressing(false); 
    } 
  };
  
  // ギャラリー画像の削除
  const handleDeleteGalleryImage = (index) => {
    const newGallery = [...locationForm.galleryImages];
    newGallery.splice(index, 1);
    setLocationForm(prev => ({ ...prev, galleryImages: newGallery }));
  };
  
  const handleAddStaff = () => { setStaffForm({ id: generateId(), name: '', role: '', bio: '', imageUrl: '' }); setIsEditingStaff(true); };
  
  const handleEditStaff = (staff) => {
    setStaffForm(staff);
    setIsEditingStaff(true);
  };

  const handleDeleteStaffImage = () => {
    setStaffForm(prev => ({ ...prev, imageUrl: '' }));
  };

  const moveStaff = (index, direction) => {
    const newStaffList = [...locationForm.staff];
    if (direction === 'up' && index > 0) {
      [newStaffList[index - 1], newStaffList[index]] = [newStaffList[index], newStaffList[index - 1]];
    } else if (direction === 'down' && index < newStaffList.length - 1) {
      [newStaffList[index + 1], newStaffList[index]] = [newStaffList[index], newStaffList[index + 1]];
    }
    setLocationForm({ ...locationForm, staff: newStaffList });
  };

  const handleSaveStaff = () => { const newStaffList = [...locationForm.staff]; const index = newStaffList.findIndex(s => s.id === staffForm.id); if (index >= 0) newStaffList[index] = staffForm; else newStaffList.push(staffForm); setLocationForm({ ...locationForm, staff: newStaffList }); setIsEditingStaff(false); };
  const handleDeleteStaff = (staffId) => { setLocationForm({ ...locationForm, staff: locationForm.staff.filter(s => s.id !== staffId) }); };
  const handleChangePassword = async () => { if (newPassword !== confirmPassword || newPassword.length < 4) { setPasswordError('無効です'); return; } const updated = { ...localSettings, adminPassword: newPassword }; setLocalSettings(updated); await handleSaveSettings(updated); setNewPassword(''); setConfirmPassword(''); alert('変更しました'); };
  const handleDeleteErrorLog = async (id) => { if (window.confirm('削除しますか？')) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'system_errors', id)); };

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen ${COLORS.base} flex items-center justify-center p-4`}>
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-stone-100">
          <div className={`flex justify-center mb-6 ${COLORS.mainGreen}`}><Lock size={48} /></div>
          <h2 className={`text-2xl font-bold text-center ${COLORS.textBrown} mb-6`}>管理者ログイン</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="password" placeholder="パスワード" className="w-full p-3 border rounded-lg" value={password} onChange={(e) => setPassword(e.target.value)} />
            <button type="submit" className={`w-full ${COLORS.bgMainGreen} text-white font-bold py-3 rounded-lg hover:opacity-90`}>ログイン</button>
          </form>
          <button onClick={onBackToHome} className="w-full mt-4 text-gray-500 text-sm hover:underline text-center">サイトに戻る</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col font-sans`}>
      <header className={`bg-gray-800 text-white p-4 shadow-md`}>
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2"><Settings /><span className="font-bold text-lg">管理者画面</span></div>
          <div className="flex gap-4">
            <button onClick={onBackToHome} className="flex items-center text-sm hover:text-green-200"><Globe size={16} className="mr-1" /> サイト表示</button>
            <button onClick={() => setIsAuthenticated(false)} className="flex items-center text-sm hover:text-red-200"><LogOut size={16} className="mr-1" /> ログアウト</button>
          </div>
        </div>
      </header>
      <div className="flex flex-1 container mx-auto p-4 gap-6 max-w-6xl">
        <aside className="w-full md:w-64 bg-white rounded-xl shadow-sm h-fit border border-gray-200">
          <nav className="p-2 space-y-1">
            {[
              { id: 'news', label: 'お知らせ管理', icon: TreeDeciduous },
              { id: 'locations', label: '事業所・スタッフ', icon: Building },
              { id: 'links', label: 'リンク管理', icon: LinkIcon },
              { id: 'images', label: '画像設定', icon: ImageIcon },
              { id: 'inquiries', label: 'お問い合わせ', icon: MessageSquare },
              { id: 'errorlogs', label: 'エラーログ', icon: FileWarning }, 
              { id: 'email', label: 'メール設定', icon: Mail },
              { id: 'seo', label: 'SEO設定', icon: Globe },
              { id: 'security', label: 'セキュリティ', icon: Key },
            ].map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center p-3 rounded-lg font-bold transition-colors ${activeTab === tab.id ? 'bg-gray-200 text-gray-900' : `text-gray-600 hover:bg-gray-50`}`}>
                <tab.icon size={20} className="mr-3" /> {tab.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="flex-1 bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-200 overflow-y-auto">
          {/* --- 各タブのコンテンツ --- */}
          {activeTab === 'news' && (
            <div className="animate-fade-in">
               <div className="flex justify-between items-center mb-4">
                 <h3 className="text-xl font-bold">{editingNewsId ? 'お知らせ編集' : 'お知らせ投稿'}</h3>
                 {editingNewsId && <button onClick={handleCancelEdit} className="text-sm text-gray-500 underline">編集をキャンセル</button>}
               </div>
               <form onSubmit={handleNewsSubmit} className="space-y-4 max-w-2xl">
                 <input type="date" required className="w-full p-2 border rounded" value={newsForm.date} onChange={e=>setNewsForm({...newsForm, date: e.target.value})}/>
                 <input type="text" placeholder="タイトル" required className="w-full p-2 border rounded" value={newsForm.title} onChange={e=>setNewsForm({...newsForm, title: e.target.value})}/>
                 <textarea placeholder="本文" required rows="5" className="w-full p-2 border rounded" value={newsForm.content} onChange={e=>setNewsForm({...newsForm, content: e.target.value})}/>
                 <div className="border p-4 rounded bg-gray-50">
                    <label className="block mb-2 font-bold text-sm">画像添付 (任意)</label>
                    <input type="file" onChange={(e) => handleImageUpload(null, e, false, true)} />
                    {isCompressing && <p className="text-sm text-blue-500 mt-2">圧縮中...</p>}
                    {uploadError && <p className="text-sm text-red-500 mt-2">{uploadError}</p>}
                    {newsForm.imageUrl && <div className="mt-2"><img src={newsForm.imageUrl} className="h-32 object-cover rounded border" alt="Preview" /><button type="button" onClick={() => setNewsForm({...newsForm, imageUrl: ''})} className="text-red-500 text-xs mt-1 underline">画像を削除</button></div>}
                 </div>
                 <div className="flex gap-4">
                   <button type="submit" className={`${editingNewsId ? 'bg-blue-600' : 'bg-green-600'} text-white px-6 py-2 rounded font-bold hover:opacity-90`}>{editingNewsId ? '更新する' : '投稿'}</button>
                   {editingNewsId && <button type="button" onClick={handleCancelEdit} className="bg-gray-300 text-gray-700 px-6 py-2 rounded font-bold hover:bg-gray-400">キャンセル</button>}
                 </div>
               </form>
               <div className="mt-12 border-t pt-8">
                 <h4 className="font-bold text-lg mb-4">過去のお知らせ一覧</h4>
                 <div className="space-y-4">
                   {newsList.length === 0 ? <p className="text-gray-400">まだお知らせはありません</p> : 
                     newsList.map(item => (
                       <div key={item.id} className="border p-4 rounded bg-white flex justify-between items-start hover:shadow-sm transition-shadow">
                         <div className="flex-1 mr-4">
                            <div className="flex items-center gap-2 mb-1"><span className="font-bold text-sm text-gray-500">{item.date}</span>{item.imageUrl && <ImageIcon size={14} className="text-gray-400" />}</div>
                            <div className="font-bold mb-1">{item.title}</div>
                            <div className="text-sm text-gray-600 truncate max-w-xl">{item.content}</div>
                         </div>
                         <div className="flex gap-2 shrink-0">
                            <button onClick={() => handleEditNews(item)} className="bg-blue-50 text-blue-600 p-2 rounded hover:bg-blue-100" title="編集"><Edit size={18}/></button>
                            <button onClick={() => handleDeleteNews(item.id)} className="bg-red-50 text-red-600 p-2 rounded hover:bg-red-100" title="削除"><Trash2 size={18}/></button>
                         </div>
                       </div>
                     ))
                   }
                 </div>
               </div>
            </div>
          )}

          {/* リンク管理タブ */}
          {activeTab === 'links' && (
            <div className="animate-fade-in">
               <div className="flex justify-between items-center mb-4">
                 <h3 className="text-xl font-bold">{editingLinkId ? 'リンク編集' : 'リンク追加'}</h3>
                 {editingLinkId && <button onClick={handleCancelLinkEdit} className="text-sm text-gray-500 underline">編集をキャンセル</button>}
               </div>
               <form onSubmit={handleLinkSubmit} className="space-y-4 max-w-2xl">
                 <input type="text" placeholder="リンクタイトル（例：採用情報）" required className="w-full p-2 border rounded" value={linkForm.title} onChange={e=>setLinkForm({...linkForm, title: e.target.value})}/>
                 <input type="text" placeholder="URL（例：https://example.com/recruit）" required className="w-full p-2 border rounded" value={linkForm.url} onChange={e=>setLinkForm({...linkForm, url: e.target.value})}/>
                 <div className="border p-4 rounded bg-gray-50">
                    <label className="block mb-2 font-bold text-sm">バナー画像</label>
                    <input type="file" onChange={(e) => handleImageUpload(null, e, false, false, true)} />
                    {isCompressing && <p className="text-sm text-blue-500 mt-2">圧縮中...</p>}
                    {uploadError && <p className="text-sm text-red-500 mt-2">{uploadError}</p>}
                    {linkForm.imageUrl && <div className="mt-2"><img src={linkForm.imageUrl} className="h-32 object-cover rounded border" alt="Preview" /><button type="button" onClick={() => setLinkForm({...linkForm, imageUrl: ''})} className="text-red-500 text-xs mt-1 underline">画像を削除</button></div>}
                 </div>
                 <div className="flex gap-4">
                   <button type="submit" className={`${editingLinkId ? 'bg-blue-600' : 'bg-green-600'} text-white px-6 py-2 rounded font-bold hover:opacity-90`}>{editingLinkId ? '更新する' : '追加'}</button>
                 </div>
               </form>
               <div className="mt-12 border-t pt-8">
                 <h4 className="font-bold text-lg mb-4">登録済みリンク一覧</h4>
                 <div className="space-y-4">
                   {linksList.length === 0 ? <p className="text-gray-400">登録されたリンクはありません</p> : 
                     linksList.map(item => (
                       <div key={item.id} className="border p-4 rounded bg-white flex justify-between items-center hover:shadow-sm transition-shadow">
                         <div className="flex items-center gap-4">
                            {item.imageUrl ? <img src={item.imageUrl} className="w-24 h-16 object-cover rounded border" alt="banner"/> : <div className="w-24 h-16 bg-gray-100 flex items-center justify-center rounded border text-gray-400 text-xs">No Image</div>}
                            <div>
                               <div className="font-bold">{item.title}</div>
                               <div className="text-xs text-gray-500 truncate max-w-xs">{item.url}</div>
                            </div>
                         </div>
                         <div className="flex gap-2">
                            <button onClick={() => handleEditLink(item)} className="bg-blue-50 text-blue-600 p-2 rounded hover:bg-blue-100" title="編集"><Edit size={18}/></button>
                            <button onClick={() => handleDeleteLink(item.id)} className="bg-red-50 text-red-600 p-2 rounded hover:bg-red-100" title="削除"><Trash2 size={18}/></button>
                         </div>
                       </div>
                     ))
                   }
                 </div>
               </div>
            </div>
          )}
          
          {/* その他のタブ */}
          {activeTab === 'inquiries' && (
             <div className="space-y-4">
               <div className="flex space-x-2 border-b mb-6">
                 <button className={`px-4 py-2 font-bold ${inquirySubTab === 'list' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`} onClick={() => setInquirySubTab('list')}>受信トレイ</button>
                 <button className={`px-4 py-2 font-bold ${inquirySubTab === 'settings' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`} onClick={() => setInquirySubTab('settings')}>フォーム設定</button>
               </div>
               {inquirySubTab === 'list' ? (
                 <div className="space-y-4">
                   {inquiries.length === 0 ? <p className="text-gray-400 text-center py-10">まだお問い合わせはありません</p> : inquiries.map(iq => (
                     <div key={iq.id} className="border p-4 rounded bg-gray-50 hover:bg-white transition-colors">
                       <div className="flex justify-between mb-2">
                         <span className="text-xs text-gray-500">{iq.createdAt?.toDate().toLocaleString()}</span>
                       </div>
                       <div className="space-y-1">
                         {Object.entries(iq).map(([key, value]) => {
                           if (['id', 'createdAt', 'status'].includes(key)) return null;
                           return (
                             <div key={key} className="text-sm">
                               <span className="font-bold text-gray-600 mr-2">{key}:</span>
                               <span className="whitespace-pre-wrap">{value}</span>
                             </div>
                           );
                         })}
                       </div>
                     </div>
                   ))}
                 </div>
               ) : (
                 <div className="space-y-6 max-w-3xl">
                   <div className="bg-white p-6 rounded border">
                     <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Settings size={20} /> フォーム基本設定</h3>
                     <div className="mb-4">
                       <label className="block text-sm font-bold mb-1">フォームタイトル</label>
                       <input type="text" className="w-full p-2 border rounded" value={editingContactConfig.title} onChange={e => setEditingContactConfig({...editingContactConfig, title: e.target.value})} />
                     </div>
                   </div>
                   <div className="bg-white p-6 rounded border">
                     <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><List size={20} /> 入力項目設定</h3>
                     <div className="space-y-4">
                       {editingContactConfig.fields.map((field, index) => (
                         <div key={field.id} className="border p-4 rounded bg-gray-50 relative group">
                           <div className="absolute top-2 right-2 flex gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                             <button onClick={() => moveField(index, 'up')} disabled={index === 0} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"><ChevronUp size={16}/></button>
                             <button onClick={() => moveField(index, 'down')} disabled={index === editingContactConfig.fields.length - 1} className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"><ChevronDown size={16}/></button>
                             <button onClick={() => removeField(index)} className="p-1 hover:bg-red-100 text-red-500 rounded ml-2"><Trash2 size={16}/></button>
                           </div>
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-10">
                             <div>
                               <label className="block text-xs font-bold mb-1">項目名 (ラベル)</label>
                               <input type="text" className="w-full p-2 border rounded text-sm" value={field.label} onChange={e => updateField(index, 'label', e.target.value)} />
                             </div>
                             <div>
                               <label className="block text-xs font-bold mb-1">入力タイプ</label>
                               <select className="w-full p-2 border rounded text-sm" value={field.type} onChange={e => updateField(index, 'type', e.target.value)}>
                                 <option value="text">1行テキスト</option>
                                 <option value="email">メールアドレス</option>
                                 <option value="textarea">複数行テキスト</option>
                                 <option value="select">セレクトボックス</option>
                               </select>
                             </div>
                           </div>
                           <div className="mt-3 flex items-center gap-4">
                             <label className="flex items-center text-sm cursor-pointer">
                               <input type="checkbox" className="mr-2" checked={field.required} onChange={e => updateField(index, 'required', e.target.checked)} />
                               <span className="font-bold">必須項目にする</span>
                             </label>
                             {field.type === 'select' && (
                               <label className="flex items-center text-sm cursor-pointer ml-4">
                                 <input type="checkbox" className="mr-2" checked={field.useLocationList} onChange={e => updateField(index, 'useLocationList', e.target.checked)} />
                                 <span className="text-green-700 font-bold">事業所リストを使用</span>
                               </label>
                             )}
                           </div>
                           {field.type === 'select' && !field.useLocationList && (
                             <div className="mt-2">
                               <label className="block text-xs font-bold mb-1">選択肢 (カンマ区切り)</label>
                               <input type="text" className="w-full p-2 border rounded text-sm" placeholder="例: 選択A, 選択B, 選択C" value={field.options || ''} onChange={e => updateField(index, 'options', e.target.value)} />
                             </div>
                           )}
                         </div>
                       ))}
                     </div>
                     <button onClick={addField} className="mt-4 w-full py-2 border-2 border-dashed border-gray-300 rounded text-gray-500 hover:border-blue-500 hover:text-blue-500 font-bold flex items-center justify-center gap-2">
                       <Plus size={18} /> 新しい項目を追加
                     </button>
                   </div>
                   <div className="flex justify-end">
                     <button onClick={handleSaveContactConfig} disabled={saveStatus === 'saving'} className="bg-blue-600 text-white px-8 py-3 rounded font-bold shadow hover:opacity-90">
                       {saveStatus === 'saving' ? '保存中...' : '設定を保存する'}
                     </button>
                   </div>
                 </div>
               )}
             </div>
          )}

          {activeTab === 'locations' && (
             <div>
               {!showLocationForm ? (
                 <>
                   <div className="flex justify-between items-center mb-6">
                     <h3 className="text-xl font-bold">事業所一覧</h3>
                     <button onClick={() => { resetLocationForm(); setShowLocationForm(true); }} className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold flex items-center hover:opacity-90"><Plus size={16} className="mr-2" /> 新規追加</button>
                   </div>
                   <div className="space-y-4">
                     {locations.map(loc => (
                       <div key={loc.id} className="border p-4 rounded flex justify-between items-center">
                         <div className="flex items-center gap-3">
                           {loc.imageUrl && <img src={loc.imageUrl} className="w-12 h-12 object-cover rounded" alt={loc.name} />}
                           <span className="font-bold">{loc.name}</span>
                         </div>
                         <div className="flex gap-2">
                           <button onClick={() => handleEditLocation(loc)} className="text-blue-600 p-2"><Edit size={18} /></button>
                           <button onClick={() => handleDeleteLocation(loc.id)} className="text-red-600 p-2"><Trash2 size={18} /></button>
                         </div>
                       </div>
                     ))}
                   </div>
                 </>
               ) : (
                 <div className="space-y-6">
                   <div className="flex justify-between items-center"><h3 className="text-xl font-bold">{editingLocationId ? '編集' : '新規登録'}</h3><button onClick={resetLocationForm}>戻る</button></div>
                   <form onSubmit={handleSaveLocation} className="space-y-4">
                     <input type="text" placeholder="事業所名" className="w-full p-2 border rounded" value={locationForm.name} onChange={e=>setLocationForm({...locationForm, name: e.target.value})} />
                     <input type="text" placeholder="住所" className="w-full p-2 border rounded" value={locationForm.address} onChange={e=>setLocationForm({...locationForm, address: e.target.value})} />
                     <input type="text" placeholder="電話番号" className="w-full p-2 border rounded" value={locationForm.phone} onChange={e=>setLocationForm({...locationForm, phone: e.target.value})} />
                     
                     <div className="border p-4 rounded bg-gray-50">
                       <label className="block font-bold text-sm mb-2">事業所紹介文</label>
                       <textarea
                         placeholder="例: 明るく開放的な空間で、子どもたちがのびのびと過ごせる環境を整えています。"
                         className="w-full p-2 border rounded h-32"
                         value={locationForm.description || ''}
                         onChange={e => setLocationForm({ ...locationForm, description: e.target.value })}
                       />
                     </div>

                     <div className="border p-4 rounded bg-gray-50">
                        <label className="block mb-2 font-bold text-sm">メイン画像</label>
                        <input type="file" onChange={(e) => handleImageUpload(null, e)} />
                        {isCompressing && <p className="text-sm text-blue-500 mt-2">圧縮中...</p>}
                        {uploadError && <p className="text-sm text-red-500 mt-2">{uploadError}</p>}
                        {locationForm.imageUrl && <img src={locationForm.imageUrl} className="mt-2 h-20" alt="Location preview" />}
                     </div>

                     {/* ギャラリー画像アップロード機能の追加 */}
                     <div className="border p-4 rounded bg-gray-50">
                       <label className="block mb-2 font-bold text-sm">ギャラリー画像（複数枚可）</label>
                       <input type="file" onChange={(e) => handleImageUpload(null, e, false, false, false, true)} />
                       <div className="mt-4 flex flex-wrap gap-4">
                         {locationForm.galleryImages && locationForm.galleryImages.map((imgUrl, index) => (
                           <div key={index} className="relative group">
                             <img src={imgUrl} className="h-24 w-24 object-cover rounded border" alt={`gallery-${index}`} />
                             <button type="button" onClick={() => handleDeleteGalleryImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600">
                               <X size={12} />
                             </button>
                           </div>
                         ))}
                       </div>
                     </div>

                     <div className="border p-4 rounded bg-gray-50">
                       <div className="flex justify-between items-center mb-2"><label className="font-bold text-sm">スタッフ</label><button type="button" onClick={handleAddStaff} className="text-xs bg-white border px-2 py-1 rounded">追加</button></div>
                       {isEditingStaff ? (
                         <div className="bg-white p-3 border rounded space-y-2">
                           <input type="text" placeholder="名前" className="w-full p-2 border rounded" value={staffForm.name} onChange={e=>setStaffForm({...staffForm, name: e.target.value})} />
                           <input type="text" placeholder="役職" className="w-full p-2 border rounded" value={staffForm.role} onChange={e=>setStaffForm({...staffForm, role: e.target.value})} />
                           <textarea placeholder="自己紹介" className="w-full p-2 border rounded" value={staffForm.bio} onChange={e=>setStaffForm({...staffForm, bio: e.target.value})} />
                           <input type="file" onChange={(e)=>handleImageUpload(null,e,true)} />
                           {isCompressing && <p className="text-sm text-blue-500">圧縮中...</p>}
                           {staffForm.imageUrl && (
                             <div className="mt-2">
                               <img src={staffForm.imageUrl} className="h-10 w-10 object-cover rounded-full" alt={staffForm.name} />
                               <button type="button" onClick={handleDeleteStaffImage} className="text-red-500 text-xs ml-2 hover:underline">画像を削除</button>
                             </div>
                           )}
                           <div className="flex gap-2 justify-end"><button type="button" onClick={()=>setIsEditingStaff(false)}>キャンセル</button><button type="button" onClick={handleSaveStaff} className="bg-blue-600 text-white px-3 py-1 rounded">確定</button></div>
                         </div>
                       ) : (
                         <div className="space-y-2">
                           {locationForm.staff.map((s, index) => (
                             <div key={s.id} className="flex justify-between items-center bg-white p-2 border rounded">
                               <div className="flex items-center gap-2">
                                 {s.imageUrl && <img src={s.imageUrl} className="h-8 w-8 object-cover rounded-full" alt="" />}
                                 <span className="text-sm font-bold">{s.name}</span>
                               </div>
                               <div className="flex items-center gap-2">
                                 <button type="button" onClick={() => moveStaff(index, 'up')} disabled={index === 0} className="text-gray-500 hover:text-gray-800 disabled:opacity-30"><ChevronUp size={16} /></button>
                                 <button type="button" onClick={() => moveStaff(index, 'down')} disabled={index === locationForm.staff.length - 1} className="text-gray-500 hover:text-gray-800 disabled:opacity-30"><ChevronDown size={16} /></button>
                                 <button type="button" onClick={() => handleEditStaff(s)} className="text-blue-500 text-xs p-1 hover:bg-blue-50 rounded ml-2">編集</button>
                                 <button type="button" onClick={() => handleDeleteStaff(s.id)} className="text-red-500 text-xs p-1 hover:bg-red-50 rounded">削除</button>
                               </div>
                             </div>
                           ))}
                         </div>
                       )}
                     </div>
                     <button type="submit" disabled={saveStatus==='saving'} className="bg-green-600 text-white px-8 py-3 rounded-full font-bold w-full">{saveStatus==='saving'?'保存中':'保存'}</button>
                   </form>
                 </div>
               )}
             </div>
          )}
          {activeTab === 'images' && (
             <div className="space-y-6">
                <h3 className="font-bold text-xl">共通画像</h3>
                {['logoMarkUrl', 'logoTextUrl', 'heroImageUrl'].map(field => (
                  <div key={field} className="border p-4 rounded">
                    <label className="block font-bold mb-2 capitalize">{field}</label>
                    <div className="flex items-center gap-4">
                      {localSettings[field] ? <img src={localSettings[field]} className="h-20 object-contain" alt={field} /> : <span className="text-gray-400">未設定</span>}
                      <input type="file" onChange={(e) => handleImageUpload(field, e)} />
                    </div>
                    {isCompressing && <p className="text-sm text-blue-500 mt-2">圧縮中...</p>}
                    {uploadError && <p className="text-sm text-red-500 mt-2">{uploadError}</p>}
                  </div>
                ))}
                <div className="border p-4 rounded">
                  <label className="block font-bold mb-2">YouTube URL</label>
                  <input type="text" className="w-full p-2 border rounded" value={localSettings.heroVideoUrl || ''} onChange={(e) => setLocalSettings({...localSettings, heroVideoUrl: e.target.value})} />
                </div>
                <button onClick={() => handleSaveSettings()} className="bg-green-600 text-white px-6 py-2 rounded font-bold">保存</button>
             </div>
          )}
          {activeTab === 'errorlogs' && (
             <div className="space-y-2">
                {errorLogs.map(log => (
                  <div key={log.id} className="border border-red-200 bg-red-50 p-3 rounded text-sm">
                    <div className="flex justify-between"><span className="text-red-700 font-bold">{log.message}</span><button onClick={() => handleDeleteErrorLog(log.id)} className="text-xs">削除</button></div>
                    <div className="text-xs text-gray-500 mt-1">{log.userAgent}</div>
                  </div>
                ))}
             </div>
          )}
          {activeTab === 'security' && (
             <div className="max-w-md space-y-4">
                <h3 className="text-xl font-bold">管理者パスワード変更</h3>
                <input type="password" placeholder="新しいパスワード" className="w-full p-2 border rounded" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                <input type="password" placeholder="確認用パスワード" className="w-full p-2 border rounded" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}
                <button onClick={handleChangePassword} className="bg-blue-600 text-white px-4 py-2 rounded font-bold hover:opacity-90">変更する</button>
             </div>
          )}
          {activeTab === 'email' && (
  <div className="space-y-6">
    <h3 className="text-xl font-bold">メール設定</h3>
    
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4 text-sm text-blue-800">
      <p className="font-bold mb-2">通知先設定について</p>
      <p>お問い合わせがあった際に通知を受け取るメールアドレスを設定します。<br/>複数の担当者に一斉送信することが可能です。</p>
    </div>

    <div className="border p-6 rounded bg-white">
      <label className="block font-bold mb-4">通知用メールアドレス一覧</label>
      
      <div className="space-y-3">
        {/* データ互換性のための処理: 配列がない場合は既存の単一アドレスを配列化して表示 */}
        {(localSettings.notificationEmails || (localSettings.notificationEmail ? [localSettings.notificationEmail] : []))
          .map((email, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="flex-grow relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input 
                type="email" 
                className="w-full pl-10 p-3 border rounded bg-gray-50 focus:bg-white transition-colors" 
                value={email} 
                onChange={(e) => {
                  const newEmails = [...(localSettings.notificationEmails || (localSettings.notificationEmail ? [localSettings.notificationEmail] : []))];
                  newEmails[index] = e.target.value;
                  setLocalSettings({ ...localSettings, notificationEmails: newEmails });
                }}
                placeholder="メールアドレスを入力"
              />
            </div>
            <button 
              onClick={() => {
                const newEmails = [...(localSettings.notificationEmails || (localSettings.notificationEmail ? [localSettings.notificationEmail] : []))];
                newEmails.splice(index, 1);
                setLocalSettings({ ...localSettings, notificationEmails: newEmails });
              }}
              className="p-3 text-red-500 hover:bg-red-50 rounded border border-transparent hover:border-red-100 transition-colors"
              title="削除"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>

      {/* 追加ボタン */}
      <button 
        onClick={() => {
          const currentEmails = localSettings.notificationEmails || (localSettings.notificationEmail ? [localSettings.notificationEmail] : []);
          setLocalSettings({ ...localSettings, notificationEmails: [...currentEmails, ''] });
        }}
        className="mt-4 flex items-center gap-2 text-blue-600 font-bold hover:text-blue-800 hover:bg-blue-50 px-4 py-2 rounded transition-colors"
      >
        <Plus size={18} /> アドレスを追加する
      </button>
    </div>

    <div className="flex justify-end">
      <button 
        onClick={() => handleSaveSettings()} 
        className="bg-green-600 text-white px-8 py-3 rounded font-bold hover:opacity-90 shadow-md"
      >
        設定を保存
      </button>
    </div>
  </div>
)}
          {activeTab === 'seo' && (
             <div className="space-y-6">
                <h3 className="text-xl font-bold">SEO設定</h3>
                <div className="space-y-4">
                   <div className="border p-4 rounded">
                      <label className="block font-bold mb-2">サイトタイトル (Title)</label>
                      <input type="text" className="w-full p-2 border rounded" value={localSettings.seoTitle || ''} onChange={(e) => setLocalSettings({...localSettings, seoTitle: e.target.value})} />
                   </div>
                   <div className="border p-4 rounded">
                      <label className="block font-bold mb-2">サイト説明 (Description)</label>
                      <textarea rows="3" className="w-full p-2 border rounded" value={localSettings.seoDescription || ''} onChange={(e) => setLocalSettings({...localSettings, seoDescription: e.target.value})} />
                   </div>
                   <div className="border p-4 rounded">
                      <label className="block font-bold mb-2">キーワード (Keywords)</label>
                      <input type="text" className="w-full p-2 border rounded" value={localSettings.seoKeywords || ''} onChange={(e) => setLocalSettings({...localSettings, seoKeywords: e.target.value})} />
                      <p className="text-xs text-gray-500 mt-1">カンマ区切りで入力してください</p>
                   </div>
                </div>
                <button onClick={() => handleSaveSettings()} className="bg-green-600 text-white px-6 py-2 rounded font-bold">保存</button>
             </div>
          )}
        </main>
      </div>
    </div>
  );
}

// --- Public Site Component (Brand Redesign) ---
function PublicSite({ user, onGoToAdmin, settings, locations, contactConfig }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]); 
  const [contactForm, setContactForm] = useState({}); 
  const [formStatus, setFormStatus] = useState('idle');
  const [selectedNews, setSelectedNews] = useState(null);

  const heroVideoId = getYouTubeId(settings.heroVideoUrl);

  useEffect(() => {
    if (!user) return;
    
    const newsRef = collection(db, 'artifacts', appId, 'public', 'data', 'news');
    const unsubscribeNews = onSnapshot(newsRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      items.sort((a, b) => new Date(b.date) - new Date(a.date));
      setNewsItems(items);
    });

    const linksRef = collection(db, 'artifacts', appId, 'public', 'data', 'external_links');
    const unsubscribeLinks = onSnapshot(linksRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExternalLinks(items);
    });

    return () => {
      unsubscribeNews();
      unsubscribeLinks();
    };
  }, [user]);

  // 動的フォームの初期値設定
  useEffect(() => {
    if (contactConfig && contactConfig.fields) {
      const initialForm = {};
      contactConfig.fields.forEach(field => {
        initialForm[field.id] = '';
      });
      setContactForm(initialForm);
    }
  }, [contactConfig]);

const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setFormStatus('submitting');
    
    const submitData = {};
    let messageBody = `<h1>Tree Kids School Choice: 新しいお問い合わせ</h1>`;
    messageBody += `<p>Webサイトから新しいお問い合わせがありました。</p><hr/>`;

    contactConfig.fields.forEach(field => {
      submitData[field.label] = contactForm[field.id];
      // メール本文用HTMLの構築
      messageBody += `<p><strong>${field.label}:</strong><br/>${contactForm[field.id]}</p>`;
    });

    try {
      // 1. 管理画面表示用に保存
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'inquiries_admin_view'), { ...submitData, status: 'unread', createdAt: serverTimestamp() });
      // ユーザーごとの履歴にも保存
      await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'messages'), { ...submitData, createdAt: serverTimestamp() });

      // 2. Trigger Email (mailコレクション) へ送信依頼
      // 複数のアドレス(notificationEmails)がある場合はそれを使い、なければ単一(notificationEmail)を使う
      // それもなければデフォルトのアドレスを使う
      let recipients = [];
      if (settings.notificationEmails && Array.isArray(settings.notificationEmails) && settings.notificationEmails.length > 0) {
        // 空文字を除去してリスト化
        recipients = settings.notificationEmails.filter(email => email && email.trim() !== '');
      } else if (settings.notificationEmail) {
        recipients = [settings.notificationEmail];
      } else {
        recipients = ['info@treekids-choice.com'];
      }

      // Trigger Email Extensionは 'to' フィールドに配列を渡すと複数人に送信してくれます
      await addDoc(collection(db, 'mail'), {
        to: recipients, 
        message: {
          subject: `【HPお問い合わせ】${submitData['お名前'] || 'Guest'}様より`,
          html: messageBody,
        }
      });

      setFormStatus('success');
      const resetForm = {};
      contactConfig.fields.forEach(field => { resetForm[field.id] = ''; });
      setContactForm(resetForm);
      setTimeout(() => setFormStatus('idle'), 3000);
    } catch (error) { 
      console.error("Submission error:", error); 
      setFormStatus('error'); 
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const SectionTitle = ({ children, enTitle, color = COLORS.textBrown }) => (
    <div className="text-center mb-16 relative">
      <span className={`text-5xl md:text-7xl font-black ${COLORS.mainGreen} absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -z-10 tracking-widest w-full opacity-10 font-sans`}>{enTitle}</span>
      <div className="relative inline-block">
        <h2 className={`text-2xl md:text-4xl font-bold ${color} relative z-10`}>
          {children}
        </h2>
        <MarkerUnderline color="#FFD54F" className="h-4 bottom-0" />
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen texture-bg font-sans ${COLORS.textBrown} overflow-x-hidden`}>
      {/* ... Public Site Content ... */}
      <nav className={`fixed w-full bg-white/90 backdrop-blur-md shadow-sm z-50 border-b border-[#F1F8E9] transition-all duration-300`}>
        <div className="container mx-auto px-4 h-20 flex justify-between items-center">
          <div className="flex items-center space-x-3">
             <button onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3 group text-left">
              {settings.logoMarkUrl ? (
                <img src={settings.logoMarkUrl} className="h-12 w-auto object-contain transition-transform group-hover:scale-110 duration-300" alt="Logo Mark" />
              ) : (
                <div className={`w-12 h-12 ${COLORS.bgMainGreen} rounded-full flex items-center justify-center text-white shadow-inner`}><TreeDeciduous size={26} /></div>
              )}
              {settings.logoTextUrl ? (
                <img src={settings.logoTextUrl} className="h-8 md:h-10 w-auto object-contain" alt="Tree Kids School Choice" />
              ) : (
                <div className="flex flex-col">
                  <span className={`text-xs ${COLORS.mainGreen} font-bold tracking-wider`}>放課後等デイサービス</span>
                  <h1 className={`text-xl md:text-2xl font-black ${COLORS.textBrown} leading-none tracking-tight`}>Tree Kids School <span className={`${COLORS.accentRed}`}>Choice</span></h1>
                </div>
              )}
            </button>
          </div>
          <div className={`hidden md:flex items-center space-x-8 text-base font-bold ${COLORS.textBrown}`}>
            {['教育方針', '事業所紹介', 'ご利用の流れ', 'お知らせ'].map((item) => <a key={item} href={`#${item}`} className={`hover:${COLORS.mainGreen} hover:scale-105 transition-all decoration-4 underline-offset-8 decoration-[#8FC358]/30 hover:underline`}>{item}</a>)}
            <a href="#contact" className={`${COLORS.bgAccentRed} text-white px-6 py-3 rounded-full hover:scale-105 hover:shadow-lg transition-all shadow-md flex items-center border-2 border-white`}><Mail size={18} className="mr-2" /> お問い合わせ</a>
          </div>
          <button onClick={toggleMenu} className={`md:hidden ${COLORS.mainGreen}`}>{isMenuOpen ? <X size={32} /> : <Menu size={32} />}</button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden bg-[#FFFEFA] border-t p-6 shadow-xl absolute w-full animate-fade-in-down">
            <div className="flex flex-col space-y-4">
              {['教育方針', '事業所紹介', 'ご利用の流れ', 'お知らせ'].map(item => <a key={item} href={`#${item}`} onClick={toggleMenu} className="p-3 font-bold text-lg border-b border-dashed border-gray-200 text-[#5A4230]">{item}</a>)}
              <a href="#contact" onClick={toggleMenu} className={`${COLORS.bgAccentRed} text-white p-4 rounded-xl text-center font-bold shadow-md mt-4`}>お問い合わせ</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header className="relative pt-32 pb-32 md:pt-48 md:pb-48 overflow-hidden">
        <div className="absolute top-0 right-0 w-2/3 h-full bg-[#F1F8E9] rounded-bl-[200px] -z-20 opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-[#FFF3E0] rounded-tr-[150px] -z-20 opacity-50"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="md:w-1/2 space-y-8 animate-fade-in-up">
              <div className={`inline-block bg-white ${COLORS.mainGreen} border-2 ${COLORS.borderGreen} rounded-full px-5 py-2 shadow-sm transform -rotate-2`}>
                <span className={`font-bold text-sm tracking-wider`}>📍 名古屋市緑区 児童発達支援・放課後等デイサービス</span>
              </div>
              <h2 className={`text-4xl md:text-6xl font-black ${COLORS.textBrown} leading-tight`}>
                ユニークな個性を<br/>
                <span className="relative inline-block mt-2">
                   <span className="relative z-10">“天才”</span>へと育てる。
                   <MarkerUnderline />
                </span>
              </h2>
              <p className={`text-lg md:text-xl ${COLORS.textBrown} leading-loose font-medium opacity-90`}>
                Tree Kids School Choiceは、子どもたちが<br/>
                <span className="bg-[#FFF9C4] px-1">「自分らしさ」</span>を知り、自ら未来を<br/>
                <span className={`${COLORS.accentRed} font-bold text-2xl`}>「選択（Choice）」</span>する力を育む場所です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                 <a href="#contact" className={`${COLORS.bgAccentRed} text-white font-bold py-4 px-10 rounded-full shadow-[0_4px_0_rgb(180,60,30)] hover:shadow-[0_2px_0_rgb(180,60,30)] hover:translate-y-[2px] transition-all inline-flex items-center justify-center text-lg`}>
                   見学を申し込む <ChevronRight className="ml-2 w-6 h-6" />
                 </a>
              </div>
            </div>
            
            <div className="md:w-1/2 relative w-full">
              <div className="absolute inset-0 bg-[#8FC358] opacity-20 transform rotate-3 rounded-[3rem] scale-105 z-0"></div>
              <div className="aspect-video bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border-8 border-white relative group z-10 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                {heroVideoId ? (
                  <div className="absolute inset-0 w-full h-full bg-black">
                    <iframe className="w-full h-full pointer-events-none" src={`https://www.youtube.com/embed/${heroVideoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${heroVideoId}&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1`} title="Hero Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ objectFit: 'cover' }}></iframe>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                ) : settings.heroImageUrl ? (
                  <img src={settings.heroImageUrl} className="w-full h-full object-cover" alt="Main Visual" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#F1F8E9]">
                    <TreeDeciduous size={80} className="text-[#8FC358] mb-4" />
                    <span className="text-[#8FC358] font-bold opacity-50">Image Area</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <WaveSeparator color="#FFFFFF" position="bottom" />
      </header>

      {/* Policy Section */}
      <section id="教育方針" className={`py-24 bg-white relative`}>
        <div className="container mx-auto px-4 relative z-10">
          <SectionTitle enTitle="POLICY">教育方針</SectionTitle>
          <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto text-center">
            {[
              {title: '木の幹', sub: '自分らしさ', icon: TreeDeciduous, color: 'text-white', bg: 'bg-[#5A4230]', desc: '根っこを張り、揺るがない自己肯定感を育みます。'}, 
              {title: '枝葉', sub: '知識・技能', icon: Sprout, color: 'text-white', bg: 'bg-[#8FC358]', desc: '好奇心の枝を広げ、豊かな知識と経験を蓄えます。'},
              {title: 'リンゴ', sub: '夢・成果', icon: Apple, color: 'text-white', bg: 'bg-[#E65227]', desc: 'それぞれの色や形の「実」を結ぶ未来を支援します。'}
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center group cursor-default">
                <div className={`w-32 h-32 ${item.bg} rounded-full flex items-center justify-center shadow-xl mb-6 border-8 border-[#FFFEFA] group-hover:scale-110 transition-transform duration-300 relative`}>
                  <item.icon className={`w-14 h-14 ${item.color}`} />
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#FDD835] rounded-full border-2 border-white"></div>
                </div>
                <h3 className={`text-2xl font-black ${COLORS.textBrown} mb-2`}>{item.title}</h3>
                <span className={`text-sm font-bold ${COLORS.mainGreen} mb-4 block`}>- {item.sub} -</span>
                <p className="text-gray-600 leading-relaxed max-w-xs">{item.desc}</p>
              </div>
            ))}
          </div>
          
           <div className="mt-20 bg-[#FFF8E1] rounded-3xl p-8 md:p-12 max-w-5xl mx-auto hand-drawn-box border-2 border-[#FFE082] text-center shadow-lg relative">
             <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-[#FFCA28] text-white px-6 py-2 rounded-full font-bold shadow-md">大切にしていること</div>
             <p className={`text-lg md:text-xl font-medium ${COLORS.textBrown} leading-loose`}>
               私たちは、できないことを矯正するのではなく、<br className="hidden md:block"/>
               <span className="font-bold text-[#E65227] text-2xl mx-1">「得意なこと」</span>を徹底的に伸ばします。<br/>
               一人ひとりの凸凹（デコボコ）は、個性という名の才能の原石です。
             </p>
           </div>
        </div>
        <WaveSeparator color="#F1F8E9" position="bottom" />
      </section>

      {/* Locations Section (Light Green Background) */}
      <section id="事業所紹介" className="py-24 bg-[#F1F8E9] relative">
        <div className="container mx-auto px-4 relative z-10">
          <SectionTitle enTitle="LOCATIONS">事業所・スタッフ紹介</SectionTitle>
          {locations.length === 0 ? (
             <div className="text-center text-gray-400 py-10 font-bold bg-white/50 rounded-xl max-w-md mx-auto">現在準備中です。<br/>情報公開までしばらくお待ちください。</div>
          ) : (
            <div className="space-y-32">
              {locations.map((loc) => (
                <div key={loc.id} className="animate-fade-in">
                  {/* Location Card */}
                  <div className="max-w-6xl mx-auto bg-white rounded-[3rem] p-8 md:p-12 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] relative mb-20">
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-32 h-8 bg-[#E0E0E0]/50 rotate-1 shadow-sm"></div>
                    
                    <div className="flex flex-col md:flex-row gap-12 items-center">
                      <div className="md:w-1/2 order-2 md:order-1">
                        <div className="inline-block bg-[#8FC358] text-white px-4 py-1 rounded-full text-xs font-bold mb-5 tracking-widest shadow-sm">LOCATION</div>
                        <h3 className={`text-3xl md:text-4xl font-black ${COLORS.textBrown} mb-8 leading-snug`}>
                          <span className={`${COLORS.mainGreen}`}>{loc.name}</span>
                        </h3>
                        
                        <div className="space-y-4 mb-8">
                           <div className="flex items-start bg-[#F9F9F9] p-4 rounded-xl">
                              <MapPin size={24} className="mr-3 text-[#E65227] flex-shrink-0 mt-1"/>
                              <span className="font-bold text-lg">{loc.address}</span>
                           </div>
                           <div className="flex items-center bg-[#F9F9F9] p-4 rounded-xl">
                              <Phone size={24} className="mr-3 text-[#E65227] flex-shrink-0"/>
                              <span className="font-bold text-xl">{loc.phone}</span>
                           </div>
                        </div>

                        <p className={`${COLORS.textBrown} leading-loose text-lg opacity-80 whitespace-pre-wrap`}>
                          {loc.description || '明るく開放的な空間で、子どもたちがのびのびと過ごせる環境を整えています。'}
                          {loc.features && loc.features.length > 0 && ` 特徴: ${loc.features.join(', ')}`}
                        </p>
                      </div>
                      <div className="md:w-1/2 w-full order-1 md:order-2">
                        {/* Main Image */}
                        <div className="aspect-[4/3] bg-gray-100 rounded-[2rem] flex items-center justify-center overflow-hidden shadow-inner border-8 border-white transform rotate-2 mb-4">
                          {loc.imageUrl ? <img src={loc.imageUrl} className="w-full h-full object-cover" alt={loc.name} /> : <div className="text-gray-300 text-center"><ImageIcon size={48} className="mx-auto mb-2"/><span>No Image</span></div>}
                        </div>
                        
                        {/* Gallery Images (Sub images) */}
                        {loc.galleryImages && loc.galleryImages.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                             {loc.galleryImages.map((imgUrl, idx) => (
                               <div key={idx} className="aspect-square bg-gray-50 rounded-xl overflow-hidden border-2 border-white shadow-sm hover:scale-105 transition-transform cursor-pointer">
                                 <img src={imgUrl} className="w-full h-full object-cover" alt={`Gallery ${idx + 1}`} />
                               </div>
                             ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Staff Grid */}
                  {loc.staff && loc.staff.length > 0 && (
                    <div className="max-w-6xl mx-auto">
                      <div className="flex items-center justify-center mb-10">
                         <div className="h-[2px] w-12 bg-[#8FC358] rounded-full mr-4"></div>
                         <h4 className={`text-2xl font-bold ${COLORS.textBrown} flex items-center`}>
                            <Users className="mr-3 text-[#8FC358]" /> スタッフ紹介
                         </h4>
                         <div className="h-[2px] w-12 bg-[#8FC358] rounded-full ml-4"></div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4">
                        {loc.staff.map((staff) => (
                          <div key={staff.id} className="bg-white p-6 rounded-3xl shadow-lg hover:-translate-y-2 transition-transform duration-300 relative group">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#8FC358] to-[#AED581] rounded-t-3xl"></div>
                            <div className="flex flex-col items-center text-center mt-4">
                              <div className="w-24 h-24 rounded-full bg-[#F1F8E9] overflow-hidden border-4 border-white shadow-md mb-4 group-hover:scale-105 transition-transform">
                                {staff.imageUrl ? <img src={staff.imageUrl} className="w-full h-full object-cover" alt={staff.name} /> : <Users size={40} className="m-auto mt-6 text-[#C5E1A5]" />}
                              </div>
                              <span className="inline-block bg-[#FFF3E0] text-[#E65227] text-xs font-bold px-3 py-1 rounded-full mb-2">{staff.role}</span>
                              <h5 className={`text-xl font-bold ${COLORS.textBrown} mb-4`}>{staff.name}</h5>
                              <div className="w-full bg-[#F9F9F9] p-4 rounded-xl text-sm text-gray-600 leading-relaxed relative whitespace-pre-wrap">
                                <span className="absolute -top-3 left-4 text-4xl text-[#AED581] font-serif">“</span>
                                {staff.bio || '子どもたちの笑顔のために、精一杯サポートさせていただきます！'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <WaveSeparator color="#FFFEFA" position="bottom" />
      </section>

      {/* Flow Section */}
      <section id="ご利用の流れ" className={`py-24 ${COLORS.base} relative`}>
         <div className="container mx-auto px-4">
           <SectionTitle enTitle="FLOW">ご利用までの流れ</SectionTitle>
           <div className="max-w-4xl mx-auto grid md:grid-cols-4 gap-6">
              {[{step:'01', title:'お問い合わせ', icon:Phone}, {step:'02', title:'見学', icon:Eye}, {step:'03', title:'受給者証申請', icon:FileWarning}, {step:'04', title:'ご利用開始', icon:Heart}].map((item, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white border-2 border-[#E0E0E0] rounded-2xl p-6 text-center h-full hover:border-[#8FC358] transition-colors">
                    <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#8FC358] text-white font-bold px-3 py-1 rounded-full text-sm">STEP.{item.step}</span>
                    <item.icon className="mx-auto text-[#5A4230] mb-4 mt-2" size={32} />
                    <h4 className="font-bold">{item.title}</h4>
                  </div>
                  {idx < 3 && <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 text-gray-300"><ChevronRight /></div>}
                </div>
              ))}
           </div>
           <div className="text-center mt-12">
             <a href="#contact" className="inline-block border-b-2 border-[#E65227] text-[#E65227] font-bold hover:opacity-70 pb-1">詳しい手続きについては、お問い合わせ時にご説明します</a>
           </div>
         </div>
         <WaveSeparator color="#F9FBE7" position="bottom" />
      </section>

      {/* News Section */}
      <section id="お知らせ" className="py-24 bg-[#F9FBE7] relative">
        <div className="container mx-auto px-4">
          <SectionTitle enTitle="NEWS">お知らせ</SectionTitle>
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-sm border-4 border-white overflow-hidden p-2">
             <div className="bg-[#FFFDE7] rounded-2xl p-6 min-h-[200px]">
               {newsItems.length === 0 ? <div className="text-center text-gray-400 py-10">現在お知らせはありません。</div> : 
                 <ul className="space-y-4">
                   {newsItems.map(item => (
                     <li 
                        key={item.id} 
                        className="flex flex-col md:flex-row md:items-center border-b border-dashed border-[#E0E0E0] pb-3 last:border-0 hover:bg-white/50 transition-colors rounded px-2 cursor-pointer"
                        onClick={() => setSelectedNews(item)}
                     >
                       <span className="font-bold text-[#8FC358] mr-4 bg-white px-2 py-1 rounded text-sm shadow-sm">{item.date}</span>
                       <span className={`font-medium ${COLORS.textBrown} flex-1`}>{item.title}</span>
                       <ChevronRight size={16} className="hidden md:block text-gray-300"/>
                     </li>
                   ))}
                 </ul>
               }
             </div>
          </div>
        </div>
      </section>

      {/* External Links Section */}
      {externalLinks.length > 0 && (
        <section id="links" className={`py-16 ${COLORS.base}`}>
          <div className="container mx-auto px-4">
            <SectionTitle enTitle="LINKS">関連サイト</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {externalLinks.map(link => (
                <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="block group hover:opacity-90 transition-opacity">
                  <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 h-full flex flex-col">
                    <div className="aspect-[16/9] bg-gray-100 flex items-center justify-center overflow-hidden relative">
                      {link.imageUrl ? (
                        <img src={link.imageUrl} alt={link.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <ExternalLink className="text-gray-300 w-12 h-12" />
                      )}
                    </div>
                    <div className="p-4 flex-1 flex items-center justify-between bg-white">
                      <span className="font-bold text-sm text-[#5A4230] line-clamp-2">{link.title}</span>
                      <ExternalLink size={16} className="text-gray-400 flex-shrink-0 ml-2" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* News Modal */}
      {selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedNews(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" onClick={() => setSelectedNews(null)}><X size={24} /></button>
            <div className="mb-6 border-b pb-4">
               <span className="inline-block bg-[#8FC358] text-white px-3 py-1 rounded-full text-sm font-bold mb-2">{selectedNews.date}</span>
               <h3 className={`text-2xl font-bold ${COLORS.textBrown}`}>{selectedNews.title}</h3>
            </div>
            
            {selectedNews.imageUrl && (
              <div className="mb-6 rounded-xl overflow-hidden border-4 border-[#F1F8E9]">
                <img src={selectedNews.imageUrl} alt="News attachment" className="w-full h-auto object-cover" />
              </div>
            )}

            <div className="whitespace-pre-wrap leading-relaxed text-gray-700">{selectedNews.content}</div>
            <div className="mt-8 text-center">
               <button className="bg-[#E0E0E0] text-gray-600 px-6 py-2 rounded-full font-bold hover:bg-gray-300 transition-colors" onClick={() => setSelectedNews(null)}>閉じる</button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" className={`py-24 bg-[#5A4230] text-white relative overflow-hidden`}>
         <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-10 rotate-180">
            <svg className="relative block w-[calc(100%+1.3px)] h-[50px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#F9FBE7"></path>
            </svg>
         </div>

         <div className="container mx-auto px-4 relative z-20 pt-10">
            <div className="grid md:grid-cols-2 gap-16 max-w-6xl mx-auto">
              <div className="space-y-8">
                <div>
                   <h2 className="text-3xl md:text-4xl font-black mb-4">お問い合わせ・アクセス</h2>
                   <p className="text-[#8FC358] font-bold text-lg">まずは見学からお気軽にどうぞ</p>
                </div>
                
                <div className="space-y-6">
                  {locations.length > 0 ? locations.map(loc => (
                    <div key={loc.id} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:bg-white/20 transition-colors">
                      <h3 className="font-bold text-xl mb-3 text-[#FFCA28]">{loc.name}</h3>
                      <p className="flex items-center mb-2 opacity-90"><MapPin size={18} className="mr-3"/> {loc.address}</p>
                      <p className="flex items-center text-2xl font-bold tracking-wider"><Phone size={24} className="mr-3 text-[#8FC358]"/> {loc.phone}</p>
                    </div>
                  )) : (
                    <div className="bg-white/10 p-6 rounded-xl"><p>事業所情報は準備中です</p></div>
                  )}
                  <div className="flex items-center gap-3 text-sm text-[#E0E0E0] bg-black/20 p-4 rounded-xl inline-block">
                     <Clock size={18} /> 営業時間：平日 10:00 ～ 18:00　／　土曜・学校休業日　10:00　～　16:00
                  </div>
                </div>
              </div>

              <div className="bg-[#FFFEFA] text-[#5A4230] p-8 md:p-10 rounded-[2.5rem] shadow-2xl relative">
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-[#E65227] rounded-full flex items-center justify-center text-white font-bold transform rotate-12 shadow-lg z-10 text-sm text-center leading-tight">
                   無料<br/>見学
                </div>
                <h3 className={`text-2xl font-black text-center mb-8 ${COLORS.mainGreen}`}>
                  {contactConfig?.title || "お問い合わせフォーム"}
                </h3>
                
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="space-y-5">
                    {contactConfig && contactConfig.fields.map(field => {
                      if (field.type === 'textarea') {
                        return (
                          <textarea
                            key={field.id}
                            placeholder={`${field.label}${field.required ? ' (必須)' : ''}`}
                            required={field.required}
                            rows="4"
                            className="w-full p-4 bg-[#F1F8E9] border-2 border-transparent focus:border-[#8FC358] rounded-xl outline-none font-bold"
                            value={contactForm[field.id] || ''}
                            onChange={e => setContactForm({ ...contactForm, [field.id]: e.target.value })}
                          />
                        );
                      } else if (field.type === 'select') {
                        return (
                          <div key={field.id} className="relative">
                            <select
                              required={field.required}
                              className="w-full p-4 bg-[#F1F8E9] border-2 border-transparent focus:border-[#8FC358] rounded-xl appearance-none outline-none font-bold text-gray-700"
                              value={contactForm[field.id] || ''}
                              onChange={e => setContactForm({ ...contactForm, [field.id]: e.target.value })}
                            >
                              <option value="">{field.label}{field.required ? ' (必須)' : ''}</option>
                              {field.useLocationList ? (
                                locations.map(loc => <option key={loc.id} value={loc.name}>{loc.name}</option>)
                              ) : (
                                field.options && field.options.split(',').map(opt => <option key={opt.trim()} value={opt.trim()}>{opt.trim()}</option>)
                              )}
                            </select>
                            <ChevronRight className="absolute right-4 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none" size={20}/>
                          </div>
                        );
                      } else {
                        return (
                          <input
                            key={field.id}
                            type={field.type}
                            placeholder={`${field.label}${field.required ? ' (必須)' : ''}`}
                            required={field.required}
                            className="w-full p-4 bg-[#F1F8E9] border-2 border-transparent focus:border-[#8FC358] rounded-xl outline-none font-bold"
                            value={contactForm[field.id] || ''}
                            onChange={e => setContactForm({ ...contactForm, [field.id]: e.target.value })}
                          />
                        );
                      }
                    })}
                  </div>

                  <button type="submit" disabled={formStatus === 'submitting'} className={`w-full py-4 rounded-full font-black text-white text-lg shadow-[0_4px_0_rgb(180,60,30)] active:shadow-none active:translate-y-[4px] transition-all ${COLORS.bgAccentRed} hover:opacity-90`}>
                    {formStatus === 'idle' ? '送信する' : '送信中...'}
                  </button>
                  {formStatus === 'success' && <div className="bg-green-100 text-green-700 p-4 rounded-xl text-center font-bold animate-bounce">送信しました！担当者よりご連絡いたします。</div>}
                  {formStatus === 'error' && <div className="bg-red-100 text-red-700 p-4 rounded-xl text-center font-bold">送信に失敗しました。時間をおいて再度お試しください。</div>}
                </form>
              </div>
            </div>
         </div>
      </section>

      <footer className={`bg-[#4E342E] text-[#BCAAA4] py-12 text-sm border-t border-[#3E2723]`}>
        <div className="container mx-auto px-4 text-center">
           <div className="mb-4">
              <span className="font-bold text-white text-lg tracking-widest">Tree Kids School Choice</span>
           </div>
           <p className="mb-8 opacity-70">お子さま一人ひとりの「選択」を大切にする放課後等デイサービス</p>
           <p className="text-xs">&copy; {new Date().getFullYear()} Tree Kids School Choice. All Rights Reserved.</p>
           <button onClick={onGoToAdmin} className="mt-8 text-xs text-[#5D4037] hover:text-white transition-colors flex items-center justify-center mx-auto gap-1"><Lock size={10} /> Admin</button>
        </div>
      </footer>
    </div>
  );
}